import { faHeart, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import {
    faBan,
    faExclamationTriangle,
    faLifeRing,
    faPencil,
    faTrash,
    faUserSlash,
} from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import { Location } from "history";
import React from "react";
import { matchPath } from "react-router";

import { BLOG_POST_COMMENT_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import { blogsCommentEditUrl, blogsEntityLikersUrl } from "@/mk2/apps/blogs/urls";
import PostMenuTrigger from "@/mk2/apps/forum/components/PostMenuTrigger";
import { FORUM_COMMENT_MENU_MORE } from "@/mk2/apps/forum/helpers";
import {
    forumCommentEditUrl,
    forumPostCommentLikersUrl,
    forumPostDetailUrl,
} from "@/mk2/apps/forum/urls";
import { groupsCommentEditUrl, groupsEntityLikersUrl } from "@/mk2/apps/groups/urls";
import { heartsUrl } from "@/mk2/apps/hearts/urls";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { url } from "@/mk2/helpers/urls";
import { CommentEntity, UserEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    AUTOMATIC_COMMENT_VIOLATES_RULES,
    BLOCK_ANONYMOUS_USER,
    BLOCK_USER,
    CANCEL_EXCLUSION_FROM_DISCUSSION,
    DELETE,
    EXCLUDE_FROM_DISCUSSION,
    FORUM_COMMENT_EDIT_MENU,
    FORUM_COMMENT_MOVE_UP,
    SEND_HEART_TO_username,
    SHOW_USERS_WHO_LIKED_THIS_POST,
    UNBLOCK_USER,
} from "@/mk/autogenerated/translation";
import { isAnonymousUser, isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { adminclub_users_communication_url } from "@/mk/urls/functions";

import styles from "./CommentMenu.mscss";

interface OwnProps {
    className?: string;
    comment: CommentEntity;
    inGroups: boolean;
    isGroupPost?: boolean;
    role: "author" | "moderator" | "admin" | null;
    requestUser: UserEntity;
    location: Location;
    hasAdminclub: boolean;
    hasReport: boolean;
    isMyAnonymousComment?: boolean;
    hasEdit: boolean;
    hasDelete: boolean;
    hasMoveUp: boolean;
    hasBlockUser?: boolean;
    blockedUserIds?: number[];
    myBlockedUserIds?: number[];
    secretBlockedAnonHashes?: string[];
    hasThreadBlock?: boolean;
    threadBlockedUserIds?: number[];
    threadBlockedAnonHashes?: string[];

    onToggleMenuMore(commentId: number);
    onReport(
        entityType: ComplaintTargetType,
        commentId: number,
        location: HistoryLocationWithState,
    );
    onDelete(commentId: number);
    onMoveUp?(commentId: number);
    onSendHeart?();
    onBlockUser?(userId: number, username: string);
    onBlockAnonymousUser?(anonAuthorHash: string);
    onThreadBlockUser?(userId: number, username: string);
    onThreadBlockAnonymousUser?(anonAuthorHash: string);
    onSetCollapsedFalse?();
}

type Props = OwnProps;

class CommentMenu extends React.PureComponent<Props> {
    public render() {
        const {
            className,
            comment,
            inGroups,
            isGroupPost,
            role,
            location,
            hasAdminclub,
            hasReport,
            isMyAnonymousComment,
            hasEdit,
            hasDelete,
            hasMoveUp,
            hasBlockUser,
            blockedUserIds,
            myBlockedUserIds,
            secretBlockedAnonHashes,
            hasThreadBlock,
            threadBlockedUserIds,
            threadBlockedAnonHashes,
            requestUser,
            onSendHeart,
        } = this.props;

        const isAuthor = comment.authorUser.id === requestUser.id;
        const showReport =
            hasReport &&
            !isAuthor &&
            !isMyAnonymousComment &&
            !comment.deletedAt &&
            !comment.byAdmin;
        const showEdit = hasEdit && !comment.deletedAt;
        const showDelete = hasDelete && !comment.deletedAt;
        const showMoveUp = hasMoveUp && !comment.deletedAt;
        // Autor diskusie môže vylúčiť cudzieho prispievateľa (nie svoj vlastný komentár)
        const showThreadBlock =
            !!hasThreadBlock &&
            !isAuthor &&
            !isMyAnonymousComment &&
            !comment.byAdmin &&
            !comment.deletedAt &&
            isAuthenticated(requestUser) &&
            (!isAnonymousUser(comment.authorUser) || !!comment.anonymousAuthorHash);
        const showSendHeart =
            !comment.deletedAt &&
            !isAuthor &&
            !comment.byAdmin &&
            !isAnonymousUser(comment.authorUser);

        const forumPostMatch = matchPath<{
            topicSlug?: string;
            postSlug?: string;
        }>(location.pathname, { path: forumPostDetailUrl, sensitive: true, exact: true });

        const inForumPostDetail =
            forumPostMatch && forumPostMatch.params.topicSlug && forumPostMatch.params.postSlug;
        const showWhoLiked = comment.likeable.likesCount > 0 && isAuthenticated(requestUser);

        const commentDate = hasAdminclub && comment.created && new Date(comment.created);
        const commentDateString =
            commentDate &&
            commentDate
                .toLocaleDateString("sk-SK", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                })
                .replace(/\s/g, "");
        const adminclubUrl = adminclub_users_communication_url({
            message_id: comment.id,
            date_from: commentDateString,
            date_to: commentDateString,
            show_forum: "on",
        });
        const showAdminclub = hasAdminclub && adminclubUrl;

        return isAuthenticated(requestUser) &&
            (showReport ||
                showThreadBlock ||
                showSendHeart ||
                showWhoLiked ||
                showAdminclub ||
                showEdit ||
                showDelete ||
                showMoveUp) ? (
            <Menu
                className={cx(styles.CommentMenu, className)}
                menuId={
                    inGroups
                        ? BLOG_POST_COMMENT_MENU_MORE(comment.id)
                        : FORUM_COMMENT_MENU_MORE(comment.id)
                }
                trigger={
                    <PostMenuTrigger
                        className={cx(inGroups && styles["CommentMenu__trigger--inGroups"])}
                        onClick={this.handleOnToggleMenuMore}
                    />
                }
                triggerShadow={false}
            >
                {showReport && (
                    <MenuItem
                        label={AUTOMATIC_COMMENT_VIOLATES_RULES}
                        icon={faExclamationTriangle}
                        onClick={this.handleOnReport}
                    />
                )}

                {showThreadBlock &&
                    (isAnonymousUser(comment.authorUser) ? (
                        comment.anonymousAuthorHash && (
                            <MenuItem
                                label={
                                    threadBlockedAnonHashes?.includes(comment.anonymousAuthorHash)
                                        ? CANCEL_EXCLUSION_FROM_DISCUSSION
                                        : EXCLUDE_FROM_DISCUSSION
                                }
                                icon={faUserSlash}
                                onClick={this.handleOnThreadBlockAnonymousUser}
                            />
                        )
                    ) : (
                        <MenuItem
                            label={
                                threadBlockedUserIds?.includes(comment.authorUser?.id)
                                    ? CANCEL_EXCLUSION_FROM_DISCUSSION
                                    : EXCLUDE_FROM_DISCUSSION
                            }
                            icon={faUserSlash}
                            onClick={this.handleOnThreadBlockUser}
                        />
                    ))}

                {hasBlockUser &&
                    !isAuthor &&
                    !comment.byAdmin &&
                    isAuthenticated(requestUser) &&
                    (isAnonymousUser(comment.authorUser)
                        ? // Anonymous user — show "Zablokovať anonyma" if not already blocked
                          comment.anonymousAuthorHash &&
                          !secretBlockedAnonHashes?.includes(comment.anonymousAuthorHash) && (
                              <MenuItem
                                  label={BLOCK_ANONYMOUS_USER}
                                  icon={faBan}
                                  onClick={this.handleOnBlockAnonymousUser}
                              />
                          )
                        : // Regular user — existing block/unblock logic
                          // Hide if this user blocked me (but I didn't block them)
                          !(
                              blockedUserIds?.includes(comment.authorUser?.id) &&
                              !myBlockedUserIds?.includes(comment.authorUser?.id)
                          ) && (
                              <MenuItem
                                  label={
                                      myBlockedUserIds?.includes(comment.authorUser?.id)
                                          ? UNBLOCK_USER
                                          : BLOCK_USER
                                  }
                                  icon={faBan}
                                  onClick={this.handleOnBlockUser}
                              />
                          ))}

                {showSendHeart && (
                    <MenuItem
                        label={interpolate(SEND_HEART_TO_username, {
                            username: `${comment.authorUser.username}`,
                        })}
                        icon={faHeart}
                        link={
                            isAuthenticated(requestUser)
                                ? url(
                                      heartsUrl,
                                      {},
                                      { to: comment.authorUser.username, next: location.pathname },
                                  )
                                : null
                        }
                        onClick={isAuthenticated(requestUser) ? null : onSendHeart}
                        asAnchor={isAuthenticated(requestUser)}
                    />
                )}

                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon={faThumbsUp}
                        link={
                            inForumPostDetail
                                ? url(forumPostCommentLikersUrl, {
                                      topicSlug: forumPostMatch.params.topicSlug,
                                      postSlug: forumPostMatch.params.postSlug,
                                      commentId: comment.id,
                                  })
                                : isGroupPost
                                  ? url(groupsEntityLikersUrl, {
                                        type: "comment",
                                        entityId: comment.id,
                                    })
                                  : inGroups
                                    ? url(blogsEntityLikersUrl, {
                                          type: "comment",
                                          entityId: comment.id,
                                      })
                                    : null
                        }
                    />
                )}

                {showAdminclub && (
                    <MenuItem
                        label={"Adminclub"}
                        icon={faLifeRing}
                        link={adminclubUrl}
                        asAnchor
                        isAdminUI
                    />
                )}

                {showEdit && (
                    <MenuItem
                        className={styles[`CommentMenu__item--${role}`]}
                        label={FORUM_COMMENT_EDIT_MENU}
                        icon={faPencil}
                        link={url(
                            isGroupPost
                                ? groupsCommentEditUrl
                                : inGroups
                                  ? blogsCommentEditUrl
                                  : forumCommentEditUrl,
                            { commentId: comment.id },
                        )}
                    />
                )}

                {showDelete && (
                    <MenuItem
                        className={styles[`CommentMenu__item--${role}`]}
                        label={DELETE}
                        icon={faTrash}
                        onClick={this.handleOnDelete}
                    />
                )}
                {showMoveUp && (
                    <MenuItem
                        className={styles[`Comment__dropdownMenu__item--${role}`]}
                        label={FORUM_COMMENT_MOVE_UP}
                        icon={faPencil}
                        onClick={this.handleOnMoveUp}
                    />
                )}
            </Menu>
        ) : null;
    }

    private handleOnToggleMenuMore = () => {
        const { comment, onToggleMenuMore, onSetCollapsedFalse } = this.props;
        onToggleMenuMore(comment.id);
        if (onSetCollapsedFalse) {
            onSetCollapsedFalse();
        }
    };

    private handleOnReport = () => {
        const { comment, inGroups, onReport, location } = this.props;
        if (inGroups) {
            onReport(ComplaintTargetType.PhotoblogComment, comment.id, location);
        } else {
            onReport(ComplaintTargetType.ForumMessage, comment.id, location);
        }
    };

    private handleOnDelete = () => {
        const { comment, onDelete } = this.props;
        onDelete(comment.id);
    };

    private handleOnMoveUp = () => {
        const { comment, onMoveUp } = this.props;
        onMoveUp(comment.id);
    };

    private handleOnBlockUser = () => {
        const { comment, onBlockUser } = this.props;
        if (onBlockUser) {
            onBlockUser(comment.authorUser.id, comment.authorUser.username);
        }
    };

    private handleOnBlockAnonymousUser = () => {
        const { comment, onBlockAnonymousUser } = this.props;
        if (onBlockAnonymousUser && comment.anonymousAuthorHash) {
            onBlockAnonymousUser(comment.anonymousAuthorHash);
        }
    };

    private handleOnThreadBlockUser = () => {
        const { comment, onThreadBlockUser } = this.props;
        if (onThreadBlockUser) {
            onThreadBlockUser(comment.authorUser.id, comment.authorUser.username);
        }
    };

    private handleOnThreadBlockAnonymousUser = () => {
        const { comment, onThreadBlockAnonymousUser } = this.props;
        if (onThreadBlockAnonymousUser && comment.anonymousAuthorHash) {
            onThreadBlockAnonymousUser(comment.anonymousAuthorHash);
        }
    };
}

export default CommentMenu;
